import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import MDInput from "components/MDInput"; // MDInput is the custom Material-UI Input
import MDBox from "components/MDBox";
import InfoIcon from '@mui/icons-material/Info';
import MDButton from "components/MDButton"; 
import CalculateIcon from '@mui/icons-material/Calculate';
import Tooltip from '@mui/material/Tooltip';
import MDTypography from 'components/MDTypography'
import { Autocomplete, Card, Container, Divider, IconButton, Paper, Typography, Grid, TextField, RadioGroup, FormControlLabel, Radio, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const pipIncrement = {
  'AUDCAD': 0.0001,
  'AUDCHF': 0.0001,
  'AUDJPY': 0.01,
  'AUDNZD': 0.0001,
  'AUDUSD': 0.0001,
  'CADCHF': 0.0001,
  'CADJPY': 0.01,
  'CHFJPY': 0.01,
  'EURAUD': 0.0001,
  'EURCAD': 0.0001,
  'EURCHF': 0.0001,
  'EURGBP': 0.0001,
  'EURNZD': 0.0001,
  'EURUSD': 0.0001,
  'GBPAUD': 0.0001,
  'GBPCAD': 0.0001,
  'GBPCHF': 0.0001,
  'GBPJPY': 0.01,
  'GBPNZD': 0.0001,
  'GBPUSD': 0.0001,
  'NZDCAD': 0.0001,
  'NZDCHF': 0.0001,
  'NZDJPY': 0.01,
  'NZDUSD': 0.0001,
  'USDCAD': 0.0001,
  'USDCHF': 0.0001,
  'USDJPY': 0.01,
  'AUDGBP': 0.0001,
  'AUDHKD': 0.0001,
  'AUDSGD': 0.0001,
  'CHFHUF': 0.01,
  'CHFPLN': 0.0001,
  'EURAED': 0.0001,
  'EURCZK': 0.0001,
  'EURDKK': 0.0001,
  'EURHKD': 0.0001,
  'EURHUF': 0.01,
  'GBPCZK': 0.0001,
  'GBPDKK': 0.0001,
  'GBPTRY': 0.0001,
  'HKDJPY': 0.01,
  'NZDSGD': 0.0001,
  'SGDJPY': 0.01,
  'USDCNH': 0.0001,
  'USDHKD': 0.0001,
  'USDSGD': 0.0001,
  'USDTRY': 0.0001,
  'USDZAR': 0.0001,
  'ZARJPY': 0.01,
  'XAUUSD': 0.01,
  'US30': 0.01, 
  'SPX500': 0.01, 
  'NAS100': 0.01, 
  'UK100': 0.01, 
  'GER40': 0.01, 
  'XAGUSD': 0.01,  // Silver
  'XAUUSD': 0.01,  // Gold
  'XPTUSD': 0.01,  // Platinum
  'XPDUSD': 0.01,  // Palladium
  'WTIUSD': 0.01,  // Crude Oil (West Texas Intermediate)
  'BRENTUSD': 0.01, // Brent Crude Oil
  'COPPERUSD': 0.0001, // Copper
  'NGASUSD': 0.01,  // Natural Gas
  'COFFEEUSD': 0.01,  // Coffee
  'SUGARUSD': 0.0001, // Sugar
  'COTTONUSD': 0.01,  // Cotton
};

const FXBullsheetTradeOptimizer = ({}) => {
  const theme = useTheme(); 
  const [positionType, setPositionType] = useState('lot');
  const [size, setSize] = useState('');
  const [symbol, setSymbol] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  const [riskPercent, setRiskPercent] = useState('');
  const [rrTarget, setRrTarget] = useState('1:2');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [buySell, setBuySell] = useState('buy');
  const [leverage, setLeverage] = useState(1);
  const [tradeCapital, setTradeCapital] = useState(''); // New state for trade capital
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];



  useEffect(() => {
    if (entryPrice && riskPercent && rrTarget && size && tradeCapital) {
      calculateRiskLevels();
    }
  }, [entryPrice, riskPercent, rrTarget, size, positionType, symbol, buySell, leverage, tradeCapital]);

  const calculateRiskLevels = () => {
    // Special handling for different instruments
    let pipValue, contractMultiplier;
    
    if (symbol.includes('JPY')) {
      pipValue = 0.01;
      contractMultiplier = 100000; // Standard lot for JPY pairs
    } else if (symbol === 'XAUUSD') {
      pipValue = 0.1;
      contractMultiplier = 100;
    } else if (symbol === 'XAGUSD') {
      pipValue = 0.01;
      contractMultiplier = 5000;
    } else if (['US30', 'SPX500', 'NAS100', 'UK100', 'GER40'].includes(symbol)) {
      pipValue = 1;
      contractMultiplier = 1;
    } else {
      pipValue = 0.0001;
      contractMultiplier = 100000;
    }
  
    // Convert the risk percentage to a decimal
    const riskDecimal = parseFloat(riskPercent) / 100;
    
    // Split the risk-reward ratio into numerator and denominator
    const rrParts = rrTarget.split(':');
    if (rrParts.length !== 2) return; // Ensure the RR ratio is properly formatted
    
    const rrNumerator = parseFloat(rrParts[1]);
    const rrDenominator = parseFloat(rrParts[0]);
    
    // Calculate the amount of capital at risk
    const capitalRiskAmount = parseFloat(tradeCapital) * riskDecimal;
  
    // Calculate the effective position size based on the position type (lot or size)
    const effectivePositionSize = positionType === 'lot'
      ? parseFloat(size) * contractMultiplier
      : parseFloat(size) * leverage;
  
    // Calculate dollar per pip based on the instrument
    let dollarPerPip;
    if (symbol === 'XAUUSD' || symbol.includes('JPY')) {
      dollarPerPip = (pipValue * effectivePositionSize) / 100; // Adjust for XAUUSD and JPY pairs
    } else {
      dollarPerPip = pipValue * effectivePositionSize; // For most pairs
    }
  
    // Calculate risk in pips
    const riskInPips = capitalRiskAmount / dollarPerPip;
  
    // Calculate the stop loss and take profit levels based on the risk-reward ratio
    let stopLossLevel, takeProfitLevel;
    if (buySell === 'buy') {
      stopLossLevel = parseFloat(entryPrice) - (riskInPips * pipIncrement[symbol] || pipIncrement['default']);
      takeProfitLevel = parseFloat(entryPrice) + (riskInPips * pipIncrement[symbol] * rrNumerator / rrDenominator || pipIncrement['default']);
    } else {
      stopLossLevel = parseFloat(entryPrice) + (riskInPips * pipIncrement[symbol] || pipIncrement['default']);
      takeProfitLevel = parseFloat(entryPrice) - (riskInPips * pipIncrement[symbol] * rrNumerator / rrDenominator || pipIncrement['default']);
    }
  
    // Determine the number of decimals based on the symbol
    const decimals = symbol.includes('JPY') || ['XAUUSD', 'XAGUSD'].includes(symbol) ? 2 : 5;
  
    // Set the stop loss and take profit values with proper rounding
    setStopLoss(isNaN(stopLossLevel) ? '' : stopLossLevel.toFixed(decimals));
    setTakeProfit(isNaN(takeProfitLevel) ? '' : takeProfitLevel.toFixed(decimals));
  
    // Debug logs
    console.log({
      symbol,
      pipValue,
      contractMultiplier,
      effectivePositionSize,
      dollarPerPip,
      riskInPips,
      stopLossLevel,
      takeProfitLevel
    });
  };

  const symbolOptions = Object.keys(pipIncrement);
  const DisclaimerText = () => (
    <Typography
      sx={{
        color: iconColor,
        fontSize: '0.75rem',
        fontStyle: 'italic',
        marginTop: '2rem',
        padding: '1rem',
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        textAlign: 'center'
      }}
    >
      DISCLAIMER: FX Bullsheet Trade Optimizer Calculator is provided for informational purposes only. While we strive for accuracy, we cannot guarantee the precision of calculations, especially for cross-currency pairs requiring real-time exchange rates. This tool provides quick estimations based on available data. All values should be independently verified. Trading foreign exchange and other financial instruments carries significant risk. FX Bullsheet accepts no liability for any loss or damage arising from the use of this tool.
    </Typography>
  );

  const tooltipContent = `The FX Bullsheet Trade Optimizer helps you calculate optimal stop loss and take profit levels by inputting trade capital and risk percentage. Choose between lot size or dollar amount, and it supports multiple trading instruments. The tool automatically adjusts for different pip values and calculates based on your risk-reward ratio. Note: Set trade capital and risk % first for accurate calculations.`;

  return (
    <Card style={{ maxWidth: '800px', minHeight: '400px', backgroundColor: theme.palette.background.default || '#bac7db' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <CalculateIcon fontSize="large" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              FX Bullsheet Trade Optimizer
            </MDTypography>
            <Tooltip 
  title={tooltipContent}
  placement="bottom-end"
  componentsProps={{
    tooltip: {
      sx: {
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
        p: 1.5,
        maxWidth: '250px',
        fontSize: '0.75rem',
        lineHeight: 1.5,
        '& .MuiTooltip-arrow': {
          color: theme.palette.background.paper,
        }
      }
    }
  }}
>
  <InfoIcon 
    sx={{ 
      ml: 1, 
      fontSize: '1rem',
      color: iconColor,
      cursor: 'help'
    }} 
  />
</Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" flexDirection="column" pb={2} px={2}>

          {/* Trade Capital */}
          <MDBox mb={2}>
            <TextField
              label="Trade Capital"
              type="number"
              value={tradeCapital}
              onChange={(e) => setTradeCapital(e.target.value)}
              margin="normal"
              fullWidth
            />
          </MDBox>

          {/* Position Type */}
          <MDBox mb={2}>
            <TextField
              select
              label="Position Size Type"
              value={positionType}
              onChange={(e) => setPositionType(e.target.value)}
              margin="normal"
              fullWidth
            >
              <MenuItem value="lot">Lot Size</MenuItem>
              <MenuItem value="dollar">Dollar Amount</MenuItem>
            </TextField>
          </MDBox>

          {/* Buy/Sell */}
          <MDBox mb={2}>
            <TextField
              select
              label="Buy/Sell"
              value={buySell}
              onChange={(e) => setBuySell(e.target.value)}
              margin="normal"
              fullWidth
            >
              <MenuItem value="buy">Buy</MenuItem>
              <MenuItem value="sell">Sell</MenuItem>
            </TextField>
          </MDBox>
          
          {/* Size */}
          <MDBox mb={2}>
            <TextField
              label={positionType === 'lot' ? "Lot Size" : "Dollar Amount"}
              type="number"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              margin="normal"
              fullWidth
            />
          </MDBox>

          {/* Symbol Selection */}
          <MDBox mb={2}>
            <Autocomplete
              value={symbol}
              onChange={(event, newValue) => setSymbol(newValue || '')}
              inputValue={symbol}
              onInputChange={(event, newInputValue) => setSymbol(newInputValue)}
              options={symbolOptions}
              renderInput={(params) => <TextField {...params} label="Symbol" margin="normal" fullWidth />}
              isOptionEqualToValue={(option, value) => option === value}
            />
          </MDBox>

          {/* Entry Price */}
          <MDBox mb={2}>
            <TextField
              label="Entry Price"
              type="number"
              value={entryPrice}
              onChange={(e) => setEntryPrice(e.target.value)}
              margin="normal"
              fullWidth
            />
          </MDBox>

          {/* Leverage - Only show if Position Type is Dollar Amount */}
          {positionType === 'dollar' && (
            <MDBox mb={2}>
              <TextField
                label="Leverage (optional)"
                type="number"
                value={leverage}
                onChange={(e) => setLeverage(e.target.value)}
                margin="normal"
                fullWidth
              />
            </MDBox>
          )}

          {/* Risk Percentage */}
          <MDBox mb={2}>
            <TextField
              label="Risk %"
              type="number"
              value={riskPercent}
              onChange={(e) => setRiskPercent(e.target.value)}
              margin="normal"
              fullWidth
            />
          </MDBox>

          {/* RR Target */}
          <MDBox mb={2}>
            <TextField
              label="RR Target (e.g., 1:2.5)"
              value={rrTarget}
              onChange={(e) => setRrTarget(e.target.value)}
              margin="normal"
              fullWidth
            />
          </MDBox>

          {/* Stop Loss and Take Profit Levels */}
          <MDBox mb={2}>
            <MDTypography variant="h5" fontWeight="bold" fontFamily={theme.typography.fontFamily}>
              Stop Loss Target: {stopLoss}
            </MDTypography>
          </MDBox>
          <MDBox mb={2}>
            <MDTypography variant="h5" fontWeight="bold" fontFamily={theme.typography.fontFamily}>
              Take Profit Target: {takeProfit}
            </MDTypography>
          </MDBox>
          <DisclaimerText />
        </MDBox>
      </MDBox>
    </Card>
  );
};

export default FXBullsheetTradeOptimizer;